/**
 * Team: the title ("Behind HolaTech.") moves right to left in an endless loop, as the Partners marquee. Every word
 * moves by one set width (word + gap) and gsap.utils.wrap keeps x inside [-setWidth, 0), so each copy takes the place
 * of the one next to it without a jump; x = 0 is the still row (the h2 first, as in Figma).
 * - Copies are cloned from the aria-hidden one when the words do not cover the track at x = -setWidth.
 * - Slower under the mouse, pushed by the page scroll: the shared loop speed (assets/js/loop-motion.ts).
 * - Pauses off screen. ?still and prefers-reduced-motion keep the still row. GSAP: Nectar's (handle "gsap").
 */
import { loopMotion } from '../../assets/js/loop-motion';

const SPEED = 60; // px per second, right to left

function init(wrapper: HTMLElement): void {
	const { gsap } = window;
	const track = wrapper.querySelector<HTMLElement>('[data-team-track]');
	const words = Array.from(wrapper.querySelectorAll<HTMLElement>('[data-team-word]'));
	const copy = words.find((word) => word.getAttribute('aria-hidden') === 'true');
	if (!gsap || !track || words.length < 2 || !copy) {
		return;
	}
	if (new URLSearchParams(window.location.search).has('still')) {
		return;
	}

	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
	let tween: gsap.core.Tween | null = null;
	let inView = false;

	const build = (): void => {
		const progress = tween ? tween.progress() : 0;
		tween?.kill();
		tween = null;
		gsap.set(words, { clearProps: 'transform' });
		if (reduceMotion.matches) {
			return;
		}

		const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
		const setWidth = words[0].getBoundingClientRect().width + gap;
		if (setWidth <= gap) {
			return;
		}

		// At x = -setWidth the last word must still reach the right edge
		while ((words.length - 1) * setWidth < track.clientWidth + gap) {
			const clone = copy.cloneNode(true) as HTMLElement;
			track.append(clone);
			words.push(clone);
		}

		const wrapX = gsap.utils.wrap(-setWidth, 0);
		gsap.set(words, { x: wrapX(-progress * setWidth) });
		tween = gsap.to(words, {
			x: `-=${setWidth}`,
			duration: setWidth / SPEED,
			ease: 'none',
			repeat: -1,
			modifiers: { x: gsap.utils.unitize(wrapX) },
			paused: !inView,
		});
	};

	loopMotion()?.add({ hover: track, apply: (scale) => tween?.timeScale(scale) });

	// Rebuild when the track or the title change size (breakpoint, font loading)
	let frame = 0;
	const resizeObserver = new ResizeObserver(() => {
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(build);
	});
	resizeObserver.observe(track);
	resizeObserver.observe(words[0]);

	new IntersectionObserver(([entry]) => {
		inView = entry.isIntersecting;
		if (inView) {
			tween?.play();
		} else {
			tween?.pause();
		}
	}).observe(track);

	reduceMotion.addEventListener('change', build);
}

document.querySelectorAll<HTMLElement>('[data-px-block="team"]').forEach(init);
